import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { CreateGroupModal } from './CreateGroupModal';
import { EditGroupModal } from './EditGroupModal';
import { GroupMembersModal } from './GroupMembersModal';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { useSearch } from '../../../contexts/SearchContext';
import apiClient from '../../../api/axios';

interface Group {
  id: string;
  name: string;
  description: string;
  groupType: string;
  isActive: boolean;
  isPrivate: boolean;
  keycloakGroupId?: string;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

interface GroupsResponse {
  groups: Group[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface GroupFilters {
  groupType: string;
  isActive: string;
  search: string;
}

export const GroupsTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [groupFilters, setGroupFilters] = useState<GroupFilters>({
    groupType: '',
    isActive: '',
    search: ''
  });
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [groupToDeactivate, setGroupToDeactivate] = useState<Group | null>(null);
  const [groupToReactivate, setGroupToReactivate] = useState<Group | null>(null);
  const [groupForMemberManagement, setGroupForMemberManagement] = useState<Group | null>(null);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<keyof Group>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  const userPermissions = useUserPermissions();
  const canManageGroups = userPermissions.roles.includes('admin');
  const queryClient = useQueryClient();
  const { debouncedSearchQuery } = useSearch();

  // Use global search query if available, otherwise use local search term
  const effectiveSearchTerm = debouncedSearchQuery || searchTerm;

  // Fetch groups with React Query using Axios
  const { data, isLoading, error, refetch } = useQuery<GroupsResponse>({
    queryKey: ['groups', currentPage, effectiveSearchTerm, groupFilters.groupType, groupFilters.isActive],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '25',
        ...(effectiveSearchTerm && { search: effectiveSearchTerm }),
        ...(groupFilters.groupType && { groupType: groupFilters.groupType }),
        ...(groupFilters.isActive && { isActive: groupFilters.isActive })
      });

      const response = await apiClient.get(`/admin/groups?${params}`);
      return response.data;
    },
    staleTime: 30000,
  });

  // Mutations
  const deactivateGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      await apiClient.patch(`/admin/groups/${groupId}/deactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setGroupToDeactivate(null);
    }
  });

  const reactivateGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      await apiClient.patch(`/admin/groups/${groupId}/reactivate`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setGroupToReactivate(null);
    }
  });

  const deleteGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      await apiClient.delete(`/admin/groups/${groupId}/permanent`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      setGroupToDelete(null);
    }
  });

  // Filtered and sorted groups
  const filteredGroups = React.useMemo(() => {
    if (!data?.groups) return [];
    
    const filtered = data.groups.filter((group) => {
      const matchesSearch = !effectiveSearchTerm || 
        group.name.toLowerCase().includes(effectiveSearchTerm.toLowerCase()) ||
        group.description.toLowerCase().includes(effectiveSearchTerm.toLowerCase());
      
      const matchesType = !groupFilters.groupType || group.groupType === groupFilters.groupType;
      const matchesStatus = !groupFilters.isActive || group.isActive.toString() === groupFilters.isActive;
      
      return matchesSearch && matchesType && matchesStatus;
    });

    // Sort groups
    filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === undefined) return sortDirection === 'asc' ? -1 : 1;
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [data?.groups, effectiveSearchTerm, groupFilters, sortField, sortDirection]);

  const handleSort = (field: keyof Group) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSelectAll = () => {
    if (selectedGroups.size === filteredGroups.length) {
      setSelectedGroups(new Set());
    } else {
      setSelectedGroups(new Set(filteredGroups.map(g => g.id)));
    }
  };

  const handleSelectGroup = (groupId: string) => {
    const newSelected = new Set(selectedGroups);
    if (newSelected.has(groupId)) {
      newSelected.delete(groupId);
    } else {
      newSelected.add(groupId);
    }
    setSelectedGroups(newSelected);
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
        Active
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
        Inactive
      </span>
    );
  };

  const getGroupTypeBadge = (groupType: string, searchTerm?: string) => {
    const colors: { [key: string]: string } = {
      'department': 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent',
      'role': 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      'team': 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      'custom': 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[groupType] || colors.custom}`}>
        {searchTerm ? highlightText(groupType, searchTerm) : groupType}
      </span>
    );
  };

  // Utility function to highlight search terms
  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 dark:bg-yellow-800/50 px-1 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading groups...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 dark:text-red-400 mb-4">
          Failed to load groups. Please try again.
        </div>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls Bar */}
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="px-4 sm:px-6 py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
              {/* Left side - Search and Filters */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search groups by name or description..."
                    value={effectiveSearchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* Filters */}
                <div className="flex gap-2 flex-shrink-0">
                  <select
                    value={groupFilters.groupType}
                    onChange={(e) => setGroupFilters(prev => ({ ...prev, groupType: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="department">Department</option>
                    <option value="role">Role</option>
                    <option value="team">Team</option>
                    <option value="custom">Custom</option>
                  </select>
                  <select
                    value={groupFilters.isActive}
                    onChange={(e) => setGroupFilters(prev => ({ ...prev, isActive: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Right side - Count and Add button */}
              <div className="flex items-center justify-between sm:justify-start gap-3 lg:gap-4 flex-shrink-0">
                <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''}
                </div>
                
                {canManageGroups && (
                  <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
                    <PlusIcon className="h-4 w-4" />
                    <span className="hidden sm:inline">Add Group</span>
                    <span className="sm:hidden">Add</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Bulk actions bar */}
        {selectedGroups.size > 0 && (
          <div className="bg-lex-accent-soft border-b border-lex-accent/30 px-6 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-lex-accent">
                {selectedGroups.size} group{selectedGroups.size !== 1 ? 's' : ''} selected
              </span>
              <div className="flex space-x-2">
                <button className="px-3 py-1 text-sm bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover">
                  Export
                </button>
                <button className="px-3 py-1 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700">
                  Bulk Deactivate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left w-12">
                  <input
                    type="checkbox"
                    checked={selectedGroups.size === filteredGroups.length && filteredGroups.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                  />
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('name')}
                >
                  Name
                  {sortField === 'name' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('groupType')}
                >
                  Type
                  {sortField === 'groupType' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('memberCount')}
                >
                  Members
                  {sortField === 'memberCount' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('isActive')}
                >
                  Status
                  {sortField === 'isActive' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('createdAt')}
                >
                  Created
                  {sortField === 'createdAt' && (
                    <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-12">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredGroups.map((group) => (
                <tr key={group.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedGroups.has(group.id)}
                      onChange={() => handleSelectGroup(group.id)}
                      className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {highlightText(group.name, effectiveSearchTerm)}
                        </div>
                        {group.keycloakGroupId && (
                          <div className="text-xs text-lex-accent">Synced to Keycloak</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                    {highlightText(group.description || '-', effectiveSearchTerm)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getGroupTypeBadge(group.groupType, effectiveSearchTerm)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div className="flex items-center">
                      <UsersIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {group.memberCount}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(group.isActive)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(group.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {canManageGroups && (
                      <div className="flex items-center justify-end gap-2">
                        {/* Primary Action: Manage Members */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setGroupForMemberManagement(group)}
                          className="flex items-center gap-1 text-green-600 hover:text-green-700 border-green-200 hover:border-green-300 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/30"
                        >
                          <UsersIcon className="h-3 w-3" />
                          <span className="text-xs font-medium">Members</span>
                        </Button>
                        
                        {/* Secondary Actions Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger>
                            <Button variant="ghost" size="sm" className="h-10 w-10 p-0 hover:bg-gray-100 dark:hover:bg-gray-700">
                              <EllipsisVerticalIcon className="h-5 w-5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditingGroup(group)}>
                              <PencilIcon className="h-4 w-4 mr-2" />
                              Edit Group
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {group.isActive ? (
                              <DropdownMenuItem 
                                onClick={() => setGroupToDeactivate(group)}
                                className="text-yellow-600 dark:text-yellow-400"
                              >
                                <XMarkIcon className="h-4 w-4 mr-2" />
                                Deactivate Group
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => setGroupToReactivate(group)}
                                className="text-green-600 dark:text-green-400"
                              >
                                <UsersIcon className="h-4 w-4 mr-2" />
                                Reactivate Group
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => setGroupToDelete(group)}
                              className="text-red-600 dark:text-red-400"
                            >
                              <TrashIcon className="h-4 w-4 mr-2" />
                              Delete Group
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {filteredGroups.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 dark:text-gray-400 mb-4">
              {effectiveSearchTerm || groupFilters.groupType || groupFilters.isActive
                ? 'No groups match your search criteria.'
                : 'No groups found.'}
            </div>
            {canManageGroups && !effectiveSearchTerm && !groupFilters.groupType && !groupFilters.isActive && (
              <Button onClick={() => setShowCreateModal(true)}>
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Group
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {data?.pagination && data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {((data.pagination.page - 1) * data.pagination.limit) + 1} to{' '}
            {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{' '}
            {data.pagination.total} groups
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={data.pagination.page === 1}
              onClick={() => setCurrentPage(data.pagination.page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={data.pagination.page === data.pagination.totalPages}
              onClick={() => setCurrentPage(data.pagination.page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateGroupModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}

      {editingGroup && (
        <EditGroupModal
          group={editingGroup}
          isOpen={!!editingGroup}
          onClose={() => setEditingGroup(null)}
          onSuccess={() => {
            setEditingGroup(null);
            refetch();
          }}
        />
      )}

      {groupForMemberManagement && (
        <GroupMembersModal
          group={groupForMemberManagement}
          isOpen={!!groupForMemberManagement}
          onClose={() => setGroupForMemberManagement(null)}
          onSuccess={() => {
            setGroupForMemberManagement(null);
            refetch();
          }}
        />
      )}

      {/* Confirmation Dialogs */}
      {groupToDeactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Deactivate Group
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to deactivate <strong>{groupToDeactivate.name}</strong>? 
              This will disable the group and prevent its use, but members will remain associated.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setGroupToDeactivate(null)}
                disabled={deactivateGroupMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
                onClick={() => deactivateGroupMutation.mutate(groupToDeactivate.id)}
                disabled={deactivateGroupMutation.isPending}
              >
                {deactivateGroupMutation.isPending ? 'Deactivating...' : 'Deactivate Group'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {groupToReactivate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Reactivate Group
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to reactivate <strong>{groupToReactivate.name}</strong>? 
              This will enable the group and restore its functionality.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setGroupToReactivate(null)}
                disabled={reactivateGroupMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => reactivateGroupMutation.mutate(groupToReactivate.id)}
                disabled={reactivateGroupMutation.isPending}
              >
                {reactivateGroupMutation.isPending ? 'Reactivating...' : 'Reactivate Group'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {groupToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Permanently Delete Group
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to <strong>permanently delete</strong> <strong>{groupToDelete.name}</strong>? 
              This action cannot be undone and will remove the group from both EdgeLex and Keycloak.
            </p>
            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setGroupToDelete(null)}
                disabled={deleteGroupMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteGroupMutation.mutate(groupToDelete.id)}
                disabled={deleteGroupMutation.isPending}
              >
                {deleteGroupMutation.isPending ? 'Deleting...' : 'Permanently Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 